import React from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  FileCheck,
  CheckCircle,
  Shield,
  Users,
  Lightbulb,
  Award,
  ArrowRight,
  Target,
  BarChart3,
  Gauge,
  Zap,
  ClipboardList,
  Edit,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const DocumentationDevelopment_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#4a5568] via-[#647a9c] to-[#7c91b3] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-slate-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-gray-400 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <BookOpen size={18} className="text-slate-300" />
            <span>Process Excellence and Compliance</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            Documentation Development
          </h1>
          <p className="text-gray-100 text-base md:text-lg max-w-2xl mx-auto">
            Create comprehensive, standardized documentation that streamlines
            operations, ensures compliance, and supports continuous improvement
            throughout your organization.
          </p>
        </motion.div>
      </section>

      {/* What Is */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              What Is Documentation Development?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Documentation development is the process of creating,
                organizing, and managing documents that outline an
                organization's processes, policies, and procedures. These
                documents provide clear guidelines for consistent process
                execution and demonstrate compliance with industry standards and
                regulations.
              </p>
              <p>Documentation development includes:</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Standard Operating Procedures (SOPs)",
                  "Policies and Guidelines",
                  "Work Instructions and Processes",
                  "Quality Manuals",
                  "Compliance Records",
                  "Risk Assessment Documentation",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="text-slate-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-slate-100">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Key Features of Documentation Development
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: "Process Standardization",
                desc: "Ensures business processes are defined, standardized, and followed consistently.",
              },
              {
                icon: Shield,
                title: "Regulatory Compliance",
                desc: "Ensures documents meet specific requirements for legal, operational, and safety standards.",
              },
              {
                icon: FileCheck,
                title: "Audit Readiness",
                desc: "Provides well-organized documentation supporting internal and external audits.",
              },
              {
                icon: Users,
                title: "Improved Communication",
                desc: "Well-structured documentation ensures easy access and promotes better coordination.",
              },
              {
                icon: Lightbulb,
                title: "Continuous Improvement",
                desc: "Documents best practices and creates actionable plans for addressing gaps.",
              },
              {
                icon: Edit,
                title: "Document Control",
                desc: "Implements version management and document control systems for accuracy.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:border-l-4 hover:border-slate-600 transition-all"
                >
                  <Icon size={32} className="text-slate-600 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Why Documentation Development Matters for Your Business
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "Ensure Consistency and Quality",
                  desc: "Maintains consistent standards for product quality and service delivery aligned with company objectives.",
                },
                {
                  title: "Support Regulatory Compliance",
                  desc: "Ensures businesses comply with ISO certifications, FDA approvals, and environmental regulations.",
                },
                {
                  title: "Improve Efficiency and Productivity",
                  desc: "Well-documented procedures enable employees to understand processes and perform tasks effectively.",
                },
                {
                  title: "Mitigate Risks",
                  desc: "Clear documentation helps identify and address risks ensuring potential issues are handled early.",
                },
                {
                  title: "Foster Employee Engagement",
                  desc: "Providing clear guidelines allows employees to perform tasks effectively improving job satisfaction.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 p-4 bg-slate-50 rounded-lg border-l-4 border-slate-600"
                >
                  <Star
                    size={24}
                    className="text-slate-600 flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Benefits of Documentation Development
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: "Improved Operational Efficiency",
                desc: "Streamlined documentation provides clear instructions reducing mistakes and improving productivity.",
              },
              {
                icon: FileCheck,
                title: "Audit Readiness and Compliance",
                desc: "Well-organized documentation ensures demonstration of compliance with ISO standards and regulations.",
              },
              {
                icon: BarChart3,
                title: "Better Decision-Making",
                desc: "Clear documentation allows management to make data-driven decisions improving transparency.",
              },
              {
                icon: Shield,
                title: "Minimized Risk",
                desc: "Clear guidelines and procedures help mitigate risks related to non-compliance and failures.",
              },
              {
                icon: Lightbulb,
                title: "Continuous Improvement Support",
                desc: "Documented processes provide foundation for capturing lessons learned and optimization.",
              },
              {
                icon: Award,
                title: "Enhanced Knowledge Management",
                desc: "Centralized documentation preserves organizational knowledge and institutional memory.",
              },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
                >
                  <Icon size={36} className="text-slate-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Develop */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900">
              How to Develop Effective Documentation
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Define Purpose and Scope",
                  desc: "Identify key business areas requiring documentation and define the purpose.",
                },
                {
                  step: "2",
                  title: "Involve Stakeholders",
                  desc: "Engage relevant departments ensuring all perspectives are considered and aligned.",
                },
                {
                  step: "3",
                  title: "Create Clear and Concise Content",
                  desc: "Write actionable, easy-to-understand content using simple language and visual aids.",
                },
                {
                  step: "4",
                  title: "Ensure Consistency and Standardization",
                  desc: "Follow consistent format for all documents including headings, sections, and versioning.",
                },
                {
                  step: "5",
                  title: "Implement Document Control Procedures",
                  desc: "Develop procedures for creation, approval, distribution, and revision management.",
                },
                {
                  step: "6",
                  title: "Monitor and Update Regularly",
                  desc: "Review and update documents ensuring they remain aligned with regulatory changes.",
                },
              ].map((process, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-slate-50 rounded-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{process.step}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-700">{process.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-slate-100">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Industries Benefiting from Documentation Development
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Manufacturing and Quality Management",
              "Healthcare and Pharmaceuticals",
              "Financial Services and Banking",
              "Information Technology and Software",
              "Food Safety and Compliance",
              "Consulting and Professional Services",
            ].map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-slate-600 text-center"
              >
                <BookOpen size={32} className="text-slate-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#4a5568] via-[#647a9c] to-[#7c91b3] rounded-2xl p-10 text-white text-center shadow-lg"
          >
            <BookOpen size={40} className="mx-auto mb-4 text-slate-300" />
            <h3 className="text-2xl font-bold mb-3">
              Why Choose MBA Solutions for Documentation Development?
            </h3>
            <p className="text-slate-100 mb-6 text-sm leading-relaxed">
              MBA Solutions provides expert documentation development services,
              helping businesses create clear, compliant, and effective
              documents that enhance operational efficiency and ensure
              regulatory compliance.
            </p>
            <div className="bg-white/10 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-slate-300 flex-shrink-0"
                />
                ISO and Compliance Documentation Development
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-slate-300 flex-shrink-0"
                />
                Standard Operating Procedures (SOPs)
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-slate-300 flex-shrink-0"
                />
                Quality Management and Compliance Documents
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-slate-300 flex-shrink-0"
                />
                Document Control and Versioning Systems
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-slate-300 flex-shrink-0"
                />
                Training and Implementation Support
              </p>
            </div>
            <div className="space-y-2 text-sm mb-6">
              <p>
                📧 Email:{" "}
                <a
                  href="mailto:atif@mbasolutions.co"
                  className="underline hover:text-slate-200"
                >
                  atif@mbasolutions.co
                </a>
              </p>
              <p>
                🌐 Website:{" "}
                <a
                  href="https://www.mbasolutions.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-slate-200"
                >
                  www.mbasolutions.co
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white text-slate-700 font-bold hover:bg-slate-50 transition-all"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const Star = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <polygon points="12 2 15.09 9.26 23 9.26 17.19 14.31 19.29 21.74 12 16.69 4.71 21.74 6.81 14.31 1 9.27 8.91 9.26 12 2" />
  </svg>
);

export default DocumentationDevelopment_Details;
